import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { colors, statusColors, cardStyle as defaultCardStyle, buttonBase } from "./theme.js";
// ── Loading & Error States ─────────────────────────────────────────────────
export function LoadingState({ message = "Loading..." }) {
    return _jsx("p", { style: { color: colors.textSecondary, padding: 16 }, children: message });
}
export function ErrorState({ message }) {
    return _jsxs("p", { style: { color: colors.danger, padding: 16 }, children: ["Error: ", message] });
}
/**
 * Friendly empty state: a Card with a muted message and an optional call to
 * action. Single source of truth for the "nothing here yet" pattern that was
 * previously hand-rolled on every list page.
 */
export function EmptyState({ message, action, style, }) {
    return (_jsxs(Card, { style: style, children: [_jsx("p", { style: { margin: 0, color: colors.textSecondary, fontSize: "0.88rem" }, children: message }), action && _jsx("div", { style: { marginTop: 10 }, children: action })] }));
}
// ── Skeletons (loading placeholders that mirror the real layout) ────────────
let shimmerInjected = false;
function ensureShimmerStyle() {
    if (shimmerInjected)
        return;
    shimmerInjected = true;
    const sheet = document.createElement("style");
    sheet.textContent =
        "@keyframes ui-shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}";
    document.head.appendChild(sheet);
}
/** A single shimmering placeholder block. */
export function Skeleton({ width = "100%", height = 14, radius = 6, style, }) {
    useEffect(() => {
        ensureShimmerStyle();
    }, []);
    return (_jsx("span", { "aria-hidden": true, style: {
            display: "block",
            width,
            height,
            borderRadius: radius,
            background: `linear-gradient(90deg, ${colors.surfaceTertiary} 25%, ${colors.divider} 37%, ${colors.surfaceTertiary} 63%)`,
            backgroundSize: "800px 100%",
            animation: "ui-shimmer 1.4s ease-in-out infinite",
            ...style,
        } }));
}
/** A card of `rows` shimmer lines — mirrors a typical list/detail load. */
export function SkeletonList({ rows = 4 }) {
    return (_jsx("div", { role: "status", "aria-busy": "true", "aria-label": "Loading", style: { display: "grid", gap: 10 }, children: Array.from({ length: rows }).map((_, i) => (_jsx(Card, { children: _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 8 }, children: [_jsx(Skeleton, { width: "40%", height: 12 }), _jsx(Skeleton, { width: "75%", height: 16 }), _jsx(Skeleton, { width: "55%", height: 12 })] }) }, i))) }));
}
// ── Mini Spinner (inline saving indicator) ─────────────────────────────────
let styleInjected = false;
function ensureSpinnerStyle() {
    if (styleInjected) {
        return;
    }
    styleInjected = true;
    const sheet = document.createElement("style");
    sheet.textContent = `@keyframes mini-spin{to{transform:rotate(360deg)}}`;
    document.head.appendChild(sheet);
}
export function MiniSpinner({ size = 12, color: c = colors.textTertiary, }) {
    const ref = useRef(null);
    useEffect(() => {
        ensureSpinnerStyle();
    }, []);
    return (_jsx("span", { ref: ref, style: {
            display: "inline-block",
            width: size,
            height: size,
            border: `2px solid ${c}40`,
            borderTopColor: c,
            borderRadius: "50%",
            animation: "mini-spin 0.6s linear infinite",
            verticalAlign: "middle",
        } }));
}
// ── Card ───────────────────────────────────────────────────────────────────
export function Card({ children, style }) {
    return _jsx("div", { style: { ...defaultCardStyle, ...style }, children: children });
}
// ── Pill (badge / status chip / court chip) ────────────────────────────────
export function Pill({ children, color = colors.textSecondary, bg, style, }) {
    return (_jsx("span", { style: {
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: 4,
            fontSize: "0.75rem",
            fontWeight: 600,
            color: bg ? colors.surface : color,
            background: bg ?? `${color}18`,
            ...style,
        }, children: children }));
}
// ── Status Chip (reusable session/slot status pill) ────────────────────────
/** Renders a Pill with the correct colour for a session/slot status. Single source of truth. */
export function StatusChip({ status, style }) {
    return (_jsx(Pill, { color: statusColors[status] ?? colors.textTertiary, style: style, children: status }));
}
// ── Page Header ────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, help, children, }) {
    return (_jsxs("div", { style: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 14,
            gap: 8,
            flexWrap: "wrap",
        }, children: [_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 2 }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 6 }, children: [_jsx("h2", { style: { margin: 0 }, children: title }), help && _jsx(HelpTooltip, { children: help })] }), subtitle && (_jsx("div", { style: { fontSize: "0.82rem", color: colors.textSecondary, marginTop: 2 }, children: subtitle }))] }), children && (_jsx("div", { style: { display: "flex", gap: 8, alignItems: "center" }, children: children }))] }));
}
// ── Help tooltip ('?' button + click-to-open popover) ──────────────────────
export function HelpTooltip({ children }) {
    const [open, setOpen] = useState(false);
    return (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", "aria-label": "What is this page?", "aria-expanded": open, onClick: () => setOpen((v) => !v), style: {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    border: `1px solid ${colors.border}`,
                    background: open ? colors.primary : colors.surface,
                    color: open ? colors.surface : colors.textSecondary,
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    padding: 0,
                    lineHeight: 1,
                }, children: "?" }), open && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": true, onClick: () => setOpen(false), style: {
                            position: "fixed",
                            inset: 0,
                            background: "rgba(15,23,42,0.45)",
                            zIndex: 90,
                        } }), _jsxs("div", { role: "dialog", "aria-modal": "true", style: {
                            position: "fixed",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 100,
                            width: "min(92vw, 460px)",
                            maxHeight: "80vh",
                            overflowY: "auto",
                            background: colors.surface,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 14,
                            boxShadow: colors.shadowLg,
                            fontSize: "1rem",
                            color: colors.text,
                            lineHeight: 1.55,
                            padding: "20px 22px 16px",
                        }, children: [children, _jsx("div", { style: { display: "flex", justifyContent: "flex-end", marginTop: 14 }, children: _jsx("button", { type: "button", onClick: () => setOpen(false), style: {
                                        background: colors.primary,
                                        color: colors.surface,
                                        border: "none",
                                        borderRadius: 8,
                                        padding: "8px 18px",
                                        fontSize: "0.9rem",
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        minHeight: 40,
                                    }, children: "Got it" }) })] })] }))] }));
}
// ── Section (collapsible titled section) ───────────────────────────────────
export function Section({ title, help, children, style, }) {
    return (_jsxs("section", { style: { marginBottom: 20, ...style }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }, children: [_jsx("h3", { style: { fontSize: "0.95rem", margin: 0 }, children: title }), help && _jsx(HelpTooltip, { children: help })] }), children] }));
}
export function ActionButton({ children, onClick, disabled, style, type = "button" }) {
    return (_jsx("button", { type: type, onClick: onClick, disabled: disabled, style: {
            ...buttonBase,
            background: colors.primary,
            color: colors.surface,
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
            ...style,
        }, children: children }));
}
export function GhostButton({ children, onClick, disabled, style, type = "button" }) {
    return (_jsx("button", { type: type, onClick: onClick, disabled: disabled, style: {
            ...buttonBase,
            background: "none",
            border: `1px solid ${colors.primary}`,
            color: colors.primary,
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "wait" : "pointer",
            ...style,
        }, children: children }));
}
export function DangerButton({ children, onClick, disabled, style, type = "button" }) {
    return (_jsx("button", { type: type, onClick: onClick, disabled: disabled, style: {
            ...buttonBase,
            background: "none",
            border: `1px solid ${colors.danger}`,
            color: colors.danger,
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "not-allowed" : "pointer",
            ...style,
        }, children: children }));
}
